// pages/Dealers/DealerLogin.js

import React, { useState, useEffect } from "react";
import { Box, Button, TextField, Link, Typography, Paper, Alert, CircularProgress, Grow, Fade } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "context/authContext";
import useLocalStorage from "hooks/useLocalStorage";
/**
 * DealerLogin is a component that renders a login form specifically for dealers.
 * It manages the state of the login credentials, handles changes to the form fields,
 * and processes the submission of the form to log in the dealer, redirecting them
 * to the dealer dashboard upon successful login.
 * @returns {React.ReactElement} The DealerLogin component with a form for username and password.
 */
function DealerLogin() {
  const { login, currentUser, loginError, loginLoading } = useAuth();
  const navigate = useNavigate();
  const [dealerSessionToken] = useLocalStorage("sessionToken_dealer", "");
  const [credentials, setCredentials] = useState({
    username: "",
    password: ""
  });
  const [openSnackbar, setOpenSnackbar] = useState(false);

  useEffect(() => {
    if (dealerSessionToken) {
      navigate("/dealer/dashboard");
    }
  }, [dealerSessionToken, navigate]);

  useEffect(() => {
    if (loginError) {
      setOpenSnackbar(true);
    }
  }, [loginError]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const {success, sessionToken} = await login(credentials.username, credentials.password);

    if (success && sessionToken) {
      navigate("/dealer/dashboard");
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh" p={3}>

      <Paper elevation={3} style={{ padding: "20px", maxWidth: "400px", width: "100%" }}>
        <Typography variant="h4" gutterBottom align="center">
          Dealer Login
        </Typography>
        {openSnackbar && <Alert onClose={handleCloseSnackbar} severity="error">
          {loginError || "An error occurred"}
        </Alert>}
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          <Fade in={true} timeout={600}>
            <TextField
              label="Username"
              variant="outlined"
              name="username"
              size="small"
              value={credentials.username}
              onChange={handleChange}
              margin="normal"
              required
              fullWidth
              autoFocus
            />
          </Fade>
          <Fade in={true} timeout={600}>
            <TextField
              label="Password"
              variant="outlined"
              type="password"
              name="password"
              size="small"
              value={credentials.password}
              onChange={handleChange}
              margin="normal"
              required
              fullWidth
            />
          </Fade>
          <Grow in={true} timeout={600}>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              sx={{ mt: 3, mb: 2 }}
              disabled={loginLoading}
            >
              {loginLoading ? <CircularProgress size={24} /> : "Login"}
            </Button>
          </Grow>
          <Link href="#" variant="body2" display="block" textAlign="center">
            Forgot password?
          </Link>
        </Box>
      </Paper>
    </Box>
  );
}

export default DealerLogin;

